import { FC, ReactNode, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Box, Tooltip } from '@mui/material';
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import { SystemColors } from 'melp-design/style';
import { SortableItem } from './atoms';

interface Props {
  items: Array<{ id: string; content: ReactNode }>;
  onPositionChange?: (source: number, destination: number) => void;
  disabled?: boolean;
  dragTooltip?: string;
}

const DragIndicator: FC<{ height?: number; color?: string }> = ({
  height = 52,
  color = SystemColors.grey[55],
}) => {
  return (
    <Box flexShrink={0} height={height}>
      <DragIndicatorIcon
        sx={{
          height: '100%',
          color,
        }}
      />
    </Box>
  );
};

export const SortableList: FC<Props> & {
  DragIndicator: typeof DragIndicator;
} = ({ items, onPositionChange, disabled, dragTooltip }) => {
  const [order, setOrder] = useState<string[]>(items.map((item) => item.id));
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      setOrder((prev) => {
        const oldIndex = prev.indexOf(String(active.id));
        const newIndex = prev.indexOf(String(over.id));

        onPositionChange?.(oldIndex, newIndex);

        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  if (!items.length) return null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={order}
        strategy={verticalListSortingStrategy}
        disabled={disabled}
      >
        <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {order.map((id) => {
            const item = items.find((item) => item.id === id);

            return (
              <SortableItem id={id} disabled={disabled} key={id}>
                {({ isDragging }) =>
                  dragTooltip ? (
                    <Tooltip
                      title={dragTooltip}
                      open={isDragging}
                      PopperProps={{ disablePortal: true }}
                      arrow={false}
                      placement="bottom-start"
                    >
                      <div>{item?.content ?? null}</div>
                    </Tooltip>
                  ) : (
                    item?.content ?? null
                  )
                }
              </SortableItem>
            );
          })}
        </ol>
      </SortableContext>
    </DndContext>
  );
};

SortableList.DragIndicator = DragIndicator;
